import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  FormLabelProps,
  IconProps,
  Input,
  InputProps,
  InputRightElementProps,
  VStack,
} from "@chakra-ui/react";
import { AxiosInstance, AxiosRequestConfig } from "axios";
import { FormikContextType, FormikValues, useFormikContext } from "formik";
import { get, isArray } from "lodash";
import React, { useMemo } from "react";

import { parseFormApiErrorValue } from "@picovico/utils";
import { FileInputProps } from "../components";
import { FormSelectFieldProps } from "./FormSelectField";
import { FormTextEditorProps } from "./FormTextEditor";
import { SecretFieldStatus } from "./SecretInputField";
import { getFormField } from "./formSelector";

export type InputTypes =
  | "text"
  | "email"
  | "password"
  | "number"
  | "tel"
  | "select"
  | "textarea"
  | "file"
  | "checkbox"
  | "switch"
  | "otpfield"
  | "date"
  | "radio"
  | "datetime-local"
  | "time"
  | "texteditor"
  /** A write-only secret (API key, password): see `SecretInputField`. */
  | "secret";

export interface FormFieldBaseProps extends InputProps {
  name: string;
  label?: string;
  autoSelectFields?: boolean;
  type?: InputTypes;
  required?: boolean;
  inputComponent?: React.ReactElement;
  onInputChange?: (value: unknown) => void;
  helpText?: string | React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  apierror?: Record<string, any>;
  isDisabled?: boolean;
  fieldLength?: number;
  labelProps?: FormLabelProps;
  inputRightElementProps?: InputRightElementProps;
  inputIconProps?: IconProps;
  isMulti?: boolean;
  isCreatable?: boolean;
  leftAddOn?: string | React.ReactNode;
  rightAddOn?: string | React.ReactNode;
  performAsyncUpload?: {
    axios: AxiosInstance;
    requestConfig: AxiosRequestConfig;
  };
  dateFormat?: string;
  /** datetime-local only: lets the date/time pickers also be typed into directly, not just picked from the popover. */
  allowTyping?: boolean;
  /** secret only: what the server says is saved (last four, verified at). */
  secret?: SecretFieldStatus | null;
}

export type FormFieldProps = FormFieldBaseProps &
  Partial<FormSelectFieldProps> &
  Partial<FileInputProps> &
  Partial<FormTextEditorProps>;

export type BaseInputProps = Pick<
  InputProps,
  | "value"
  | "focusBorderColor"
  | "errorBorderColor"
  | "placeholder"
  | "isDisabled"
> & {
  onInputChange?: (value: unknown) => void;
  id?: string;
  onChange: FormikContextType<unknown>["handleChange"];
  onBlur: FormikContextType<unknown>["handleBlur"];
  name: string;
  type: InputTypes;
  fieldLength?: number;
  isMulti?: boolean;
  size?: InputProps["size"];
  rounded?: InputProps["rounded"];
  dateFormat?: string;
  performAsyncUpload?: {
    axios: AxiosInstance;
    requestConfig: AxiosRequestConfig;
  };
  color?: InputProps['color'];
  borderColor?: InputProps['borderColor'];
};

function FormField<Value extends FormikValues>({
  label,
  autoSelectFields = true,
  apierror: apiError,
  labelProps,
  required = false,
  isDisabled,
  rounded,
  performAsyncUpload,
  dateFormat,
  ...props
}: FormFieldProps) {
  const { errors, touched, handleBlur, handleChange, values } =
    useFormikContext<Value>();

  const apiErrorValue =
    apiError && parseFormApiErrorValue(apiError, props.name, "");

  const formField = useMemo(() => {
    const { name, inputComponent, type, placeholder, size } = props;
    const baseInputProps: BaseInputProps = {
      name,
      rounded,
      size,
      id: name,
      onBlur: handleBlur,
      type: type ?? "text",
      onChange: handleChange,
      isMulti: props.isMulti,
      isDisabled: isDisabled,
      value: get(values, name, undefined),
      focusBorderColor: props.focusBorderColor || "primary.500",
      errorBorderColor: props.errorBorderColor || "danger.500",
      borderColor: props.borderColor,
      placeholder,
      color: props.color,
      onInputChange: props.onInputChange,
      performAsyncUpload,
      dateFormat,
    };

    if (inputComponent) {
      return React.cloneElement(inputComponent, {
        ...baseInputProps,
      });
    }
    if (autoSelectFields) {
      return getFormField(type ?? "text", { ...props, label }, baseInputProps);
    }

    return <Input {...baseInputProps} />;
  }, [
    autoSelectFields,
    rounded,
    props,
    performAsyncUpload,
    handleChange,
    handleBlur,
    values,
    isDisabled,
    dateFormat,
  ]);

  const formErrors = get(errors, props.name) || apiErrorValue;
  // File inputs never fire a blur event the way a text input does, so
  // `touched` would never become true through user interaction alone —
  // show the error as soon as it exists instead of waiting on `touched`.
  const isInvalid =
    props.type === "file"
      ? Boolean(formErrors)
      : Boolean(formErrors && get(touched, props.name));

  return (
    <FormControl
      isInvalid={isInvalid}
      isDisabled={isDisabled}
      isRequired={required}
      my={2}
    >
      {label && props.type !== 'switch' && props.type !== 'file' && (
        <FormLabel {...labelProps} htmlFor={props.name}>
          {label}
        </FormLabel>
      )}
      {formField}
      {props.helpText && (
        <FormHelperText>{props.helpText}</FormHelperText>
      )}
      {isArray(formErrors) ? (
        <VStack alignItems={"start"}>
          {formErrors?.map((error, index) => (
            <FormErrorMessage key={index}>{error}</FormErrorMessage>
          ))}
        </VStack>
      ) : (
        <FormErrorMessage>{formErrors}</FormErrorMessage>
      )}
    </FormControl>
  );
}

FormField.prototype.displayName = "FormField";

export default React.memo(FormField);
